import { isEqual, omit } from 'lodash';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type QueryKey,
  type UseQueryOptions,
  type UseQueryResult,
  useQuery,
} from 'react-query';
import { useInputController } from '../hooks/useInputController.ts';
import { useFormItemScreenReaderLabelAddition } from '../item/label/useFormItemScreenReaderLabelAddition.tsx';
import { useFormItemAccessibility } from '../item/useFormItemAccessibility.tsx';
import { useFormItemEnabled } from '../item/useFormItemEnabled.ts';
import {
  AsyncSelectInputMeta,
  type AsyncSelectMeta,
} from './AsyncSelectInputMeta.tsx';
import {
  AsyncSelectInputObject,
  type IAsyncSelectInputObjectRef,
} from './AsyncSelectInputObject.tsx';
import { type SelectOption } from './SelectInput.tsx';
import { Select, type SelectProps } from './select/Select.tsx';
import { SelectReadOnly } from './select/SelectReadOnly.tsx';
import { useSelectedOptionLabel } from './select/useSelectedOptionLabel.tsx';

export interface IBaseAsyncSelectInputProps
  extends Omit<SelectProps, 'name' | 'options'> {
  name: string;
  /**
   * Se specificato, viene utilizzato per creare un campo nascosto che
   * contiene l'intero oggetto (es. `DTO` ritornato dalle API) selezionato,
   * invece che solo il valore (es. `id`)
   */
  objectName?: string;
  children?: React.ReactNode;
  readOnly?: boolean;
}

export interface IAsyncSelectInputProps<
  TParams extends object,
  TItem extends object,
  TData extends IArrayResponse<TItem>,
> extends IBaseAsyncSelectInputProps {
  queryOptions: IQueryOptionsFn<TParams, TData>;
  initialParams?: NoInfer<TParams>;
  queryFn: IQueryFn<TParams, TData>;
  asOption: (data: TItem) => SelectOption;
  onAfterChange?: (value: any, objects: TItem[] | TItem | undefined) => void;
  responseMeta?: (response: TData) => AsyncSelectMeta;
  searchByField?: keyof TItem | keyof TParams;
  /**
   * Se specificato, viene utilizzato per indicare se disabilitare la query.
   * Ad esempio, per un minimo di caratteri da digitare.
   */
  skip?: (search: string) => boolean;
  /**
   * Mostra un messaggio personalizzato quando non ci sono risultati
   */
  notFoundMessage?: (search: string | undefined) => string | undefined;
  /**
   * Permette di filtrare ulteriormente le opzioni lato frontend.
   * Per esempio può essere utilizzato per nascondere delle opzioni
   * non filtrabili dalla query/API.
   */
  filter?: (options: TItem) => boolean;
  /**
   * Footer personalizzato renderizzato in fondo al dropdown, dopo le
   * informazioni di paginazione. Riceve il contesto della select (testo
   * digitato, opzioni correnti e azioni di selezione/chiusura), ad esempio
   * per implementare la creazione di una nuova opzione.
   */
  dropdownFooter?: (
    ctx: IAsyncSelectDropdownFooterContext<TItem>,
  ) => React.ReactNode;
  /**
   * Trasforma il testo digitato nella ricerca (es. forzare il maiuscolo).
   * Se specificata, la ricerca diventa controllata.
   */
  searchTransform?: (raw: string) => string;
  /**
   * Se `true`, carica le opzioni solo alla prima apertura del dropdown
   * invece che al mount del componente.
   */
  loadOnOpen?: boolean;
}

/**
 * Contesto passato al footer personalizzato del dropdown.
 */
export interface IAsyncSelectDropdownFooterContext<TItem extends object> {
  /** Testo digitato corrente nella ricerca */
  search: string;
  /** Opzioni attualmente disponibili (es. per verificare duplicati) */
  options: AsyncSelectOption<TItem>[];
  /** `true` mentre le opzioni sono in caricamento o aggiornamento */
  isLoading: boolean;
  /** `true` se il caricamento delle opzioni è fallito */
  isError: boolean;
  /** Seleziona il valore indicato e azzera la ricerca */
  selectValue: (value: unknown) => void;
  /** Chiude il dropdown */
  closeDropdown: () => void;
}

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

export interface IPaginatedResponse<T extends object> {
  meta?: any;
  dati?: T[] | null;
}

type IArrayResponse<T extends object> = T[] | IPaginatedResponse<T>;

export type IQueryFn<TParams extends object, TData> = (
  params?: TParams,
) => Promise<TData>;

export interface AsyncSelectOption<TItem extends object> extends SelectOption {
  object: TItem;
}

// export type IGetQueryKeyFn<TParams extends object> = (params?: TParams) => readonly string[];

export type IQueryOptionsFn<TParams extends object, TData> = (
  params?: TParams,
  options?: {
    query: UseQueryOptions<TData>;
    request?: any;
  },
) => UseQueryOptions<TData>;

export function AsyncSelectInput<
  TParams extends object,
  TItem extends object,
  TData extends IArrayResponse<TItem>,
>(props: IAsyncSelectInputProps<TParams, TItem, TData>) {
  const {
    name,
    children,
    style,
    queryFn,
    initialParams,
    queryOptions,
    searchByField,
    onAfterChange,
    asOption,
    objectName,
    readOnly,
    responseMeta,
    skip,
    notFoundMessage,
    allowClear = true,
    filter,
    dropdownFooter,
    searchTransform,
    loadOnOpen,
    ...inputProps
  } = props;

  const { t } = useTranslation();
  const { enabled, visuallyDisabled } = useFormItemEnabled();

  const {
    field: { value, onChange: onFieldChange, onBlur, ref },
  } = useInputController(name, { enabled });

  // Stato del dropdown e della ricerca, condiviso con il footer custom
  const [open, setOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [search, setSearch] = useState('');

  // Query
  const [params, setParams] = useState<TParams | undefined>(initialParams);
  const previousInitialParams = useRef<TParams | undefined>({
    ...initialParams,
  } as TParams);

  // Reimpostiamo i parametri iniziali se vengono forniti e sono diversi.
  // Usiamo deep-equal per la comparazione per evitare che vengano
  // passati come nuovi oggetti.
  useEffect(() => {
    if (!isEqual(previousInitialParams.current, initialParams)) {
      previousInitialParams.current = initialParams;
      setParams(initialParams);
    }
  }, [initialParams]);

  const isQueryEnabled =
    (skip ? !skip((params as any)?.[searchByField as string] ?? '') : true) &&
    (!loadOnOpen || hasOpened);

  const internalQueryOptions = queryOptions(params, {
    query: {
      keepPreviousData: true,
      enabled: isQueryEnabled,
    },
  });

  const query = useQuery(internalQueryOptions) as UseQueryResult<TData> & {
    queryKey: QueryKey;
  };
  query.queryKey = internalQueryOptions.queryKey!;

  const mapToOption = useCallback(
    (value: TItem) => {
      return {
        ...asOption(value),
        object: value,
      };
    },
    [asOption],
  );

  const options = useMemo(() => {
    if (!query.data) return [];
    const data = Array.isArray(query.data)
      ? query.data
      : (query.data.dati ?? []);
    const options = data
      .filter(item => (filter ? filter(item) : true))
      .map(mapToOption);

    return options;
  }, [mapToOption, query.data, filter]);

  const objectRef = useRef<IAsyncSelectInputObjectRef>(null);

  // Azzera il testo digitato e, per la ricerca server-side, riporta il
  // parametro di query al valore iniziale
  const resetSearch = useCallback(() => {
    setSearch('');
    if (searchByField) {
      setParams(
        p =>
          ({
            ...p,
            [searchByField]:
              (previousInitialParams.current as any)?.[searchByField] ?? '',
          }) as TParams,
      );
    }
  }, [searchByField]);

  const onChange = useCallback(
    (rawValue: any) => {
      // Se il valore è undefined, allora lo imposto a null (https://react-hook-form.com/docs/useform/register)
      const value = rawValue ?? null;

      onFieldChange(value);
      resetSearch();

      if (onAfterChange != null || objectRef.current) {
        if (props.mode === 'multiple') {
          const objects = options
            .filter(o => value?.includes(o.value))
            .map(o => o.object);

          objectRef.current?.onChange(objects);
          onAfterChange?.(value, objects);
        } else {
          const object = options.find(o => o.value === value)?.object;
          objectRef.current?.onChange(object);
          onAfterChange?.(value, object);
        }
      }
    },
    [options, onAfterChange, onFieldChange, props.mode, resetSearch],
  );

  // Se il campo viene reimpostato, reimposto anche la ricerca
  useEffect(() => {
    if (!value) {
      setParams(initialParams);
    }
  }, [value]);

  // Traccia il testo digitato (con l'eventuale trasformazione) e, per la
  // ricerca server-side, aggiorna i parametri della query
  const onSearch = useCallback(
    (rawValue: string) => {
      const searchValue = searchTransform
        ? searchTransform(rawValue)
        : rawValue;
      setSearch(searchValue);
      if (searchByField) {
        setParams(
          p =>
            ({
              ...p,
              [searchByField]: searchValue,
            }) as TParams,
        );
      }
    },
    [searchTransform, searchByField],
  );

  const onDropdownVisibleChange = useCallback(
    (visible: boolean) => {
      setOpen(visible);
      if (visible) setHasOpened(true);
      else resetSearch();
    },
    [resetSearch],
  );

  const closeDropdown = useCallback(() => setOpen(false), []);

  const selectedLabel = useSelectedOptionLabel(
    value,
    options,
    inputProps.mode,
    'label',
  );

  // Aggiungo un label nascosto per l'accessibilità, che indica il valore
  // selezionato.
  useFormItemScreenReaderLabelAddition(readOnly ? null : selectedLabel);

  // Gestione del backspace e delete per la cancellazione del valore,
  // per supportare l'accessibilità.
  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // La guardia sulla ricerca evita di cancellare il valore selezionato
      // mentre l'utente sta correggendo il testo digitato
      if (
        (e.key === 'Delete' || e.key === 'Backspace') &&
        allowClear &&
        !search
      ) {
        onChange(null);
      }
    },
    [allowClear, search, onChange],
  );

  // Nel caso cambino le options (e.g. async select) e il valore non
  // sia presente tra le opzioni disponibili, allora devo resettare il campo
  // Non possiamo usarlo per le select asincrone, perché le options
  // sono sempre parziali
  // useFilterSelectValuesOnOptionsChange({
  //   mode: props.mode,
  //   options: query.isLoading ? null : options,
  //   value,
  //   onChange,
  // });

  const accessibility = useFormItemAccessibility();

  const notFountContent = useMemo(() => {
    if (query.isError)
      return t(
        'inputs.async-select.errore-caricamento-opzioni',
        'Errore durante il caricamento dei dati',
      );
    if (query.isLoading)
      return t('inputs.async-select.caricamento-opzioni', 'Caricamento...');
    return (
      notFoundMessage?.((params as any)?.[searchByField as string] ?? '') ??
      t('inputs.async-select.nessun-risultato', 'Nessun risultato trovato')
    );
  }, [
    notFoundMessage,
    params,
    query.isError,
    query.isLoading,
    searchByField,
    t,
  ]);

  if (readOnly) {
    return (
      <SelectReadOnly
        value={value}
        options={options ?? []}
        filterProp="label"
      />
    );
  }

  return (
    <>
      {objectName && (
        <AsyncSelectInputObject name={objectName} ref={objectRef} />
      )}
      <Select
        size="large"
        style={{ textAlign: 'left', ...style }}
        disabled={visuallyDisabled}
        // Le Select non supportano aria-required, errore di validazione Axe
        {...omit(accessibility.attributes, ['aria-required'])}
        allowClear={allowClear}
        showSearch
        onInputKeyDown={handleInputKeyDown}
        filterOption={!searchByField}
        optionFilterProp={searchByField ? undefined : 'label'}
        loading={query.isLoading || query.isFetching}
        onSearch={onSearch}
        // La ricerca è controllata solo quando va trasformato il testo
        {...(searchTransform ? { searchValue: search } : undefined)}
        // Il dropdown è controllato solo quando il footer deve poterlo chiudere
        {...(dropdownFooter ? { open } : undefined)}
        onDropdownVisibleChange={onDropdownVisibleChange}
        notFoundContent={notFountContent}
        {...inputProps}
        options={options}
        ref={ref}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        dropdownRender={menu => (
          <div>
            {menu}
            <AsyncSelectInputMeta
              meta={
                responseMeta && query.data
                  ? responseMeta(query.data)
                  : undefined
              }
            />
            {dropdownFooter?.({
              search,
              options,
              isLoading: query.isLoading || query.isFetching,
              isError: query.isError,
              selectValue: onChange,
              closeDropdown,
            })}
          </div>
        )}
      />
    </>
  );
}
